// Centralized design system for AI Listing Generator
// This provides a single source of truth for colors and typography

/**
 * TYPOGRAPHY SCALE
 * Based on a 1.25 ratio (Major Third) for visual harmony
 * Base size: 16px
 * 
 * Use these semantic sizes instead of hardcoded pixel values.
 * Import: import { typography } from '@/theme/colors';
 */
export const typography = {
  // === FONT SIZES (in pixels) ===
  fontSize: {
    // Display - Hero sections, landing pages
    display: '32px',      // 2rem - Page titles, hero text
    
    // Headings - Section structure
    h1: '24px',           // 1.5rem - Main section headers (e.g., "Data Foundation")
    h2: '20px',           // 1.25rem - Subsection headers
    h3: '18px',           // 1.125rem - Component headers
    h4: '16px',           // 1rem - Card titles, emphasized text
    
    // Body - Primary content
    body: '14px',         // 0.875rem - Default body text, form inputs
    bodySmall: '13px',    // ~0.8rem - Secondary body text, descriptions
    
    // UI Elements - Interface components
    label: '14px',        // 0.875rem - Form labels, table headers
    button: '14px',       // 0.875rem - Button text
    chip: '13px',         // ~0.8rem - Chips, badges, pills
    
    // Small - Supporting text
    caption: '12px',      // 0.75rem - Captions, timestamps, metadata
    tiny: '11px',         // ~0.69rem - Step indicators, minimal UI
    micro: '10px',        // 0.625rem - Badges count, extreme compact
  },

  // === FONT WEIGHTS ===
  fontWeight: {
    regular: 400,         // Normal body text
    medium: 500,          // Slightly emphasized, subtitles
    semibold: 600,        // Headers, labels, important text
    bold: 700,            // Strong emphasis, critical info
  },

  // === LINE HEIGHTS ===
  lineHeight: {
    tight: 1.2,           // Headings, single-line text
    snug: 1.3,            // Compact multi-line
    normal: 1.4,          // Default for body
    relaxed: 1.5,         // Comfortable reading
    loose: 1.6,           // Long-form content
  },

  // === SEMANTIC PRESETS ===
  // Ready-to-use text styles for common patterns
  presets: {
    // Page & Section Headers
    pageTitle: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    cardTitle: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    
    // Body Text
    bodyDefault: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    bodySecondary: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    
    // UI Elements
    buttonText: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    chipText: {
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    labelText: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    
    // Small Text
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.3,
    },
    metadata: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    stepIndicator: {
      fontSize: '11px',
      fontWeight: 700,
      lineHeight: 1.2,
    },
  },
};

export const colors = {
  // Primary brand colors (professional slate for UI elements and standard CTAs)
  primary: {
    main: '#475569',        // Slate gray - USE THIS FOR MOST BUTTONS/CTAs (Next, Continue, Save, etc.)
    hover: '#334155',       // Darker slate for hover states
    light: '#f1f5f9',       // Light slate for backgrounds
    dark: '#1e293b',        // Dark slate for emphasis
  },

  // Critical/Emphasis colors (red reserved for HIGH-PRIORITY actions only)
  // Use sparingly for: final purchase, critical confirmations, destructive actions
  // DO NOT use for regular navigation buttons (Next, Continue, etc.)
  cta: {
    main: '#AC1218',        // Red for CRITICAL actions only (e.g., "Generate Listing", "Purchase", "Confirm Delete")
    hover: '#8B0E14',       // Red hover state
    light: '#FEF2F2',       // Light red backgrounds
    dark: '#7F1D1D',        // Darker red
  },


  // Text colors
  text: {
    primary: '#1F2937',     // Dark headings/titles
    secondary: '#374151',   // Medium dark text
    tertiary: '#6B7280',    // Medium gray text (most common)
    placeholder: '#9CA3AF', // Light gray text/placeholders
    disabled: '#D1D5DB',    // Disabled text
  },

  // Background colors
  background: {
    paper: '#FFFFFF',       // White backgrounds
    white: '#FFFFFF',       // Explicit white (for clarity)
    default: '#FAFBFC',     // Light gray background (primary)
    light: '#F9FAFB',       // Light gray background (secondary)
    lightest: '#FAFBFC',    // Lightest gray background (same as default)
    medium: '#F3F4F6',      // Medium gray backgrounds
    neutral: '#FAFAFA',     // Alternative neutral background (for read-only fields)
    hover: '#F5F5F5',       // Hover background
  },

  // Status/State colors
  status: {
    error: {
      main: '#DC2626',      // Primary error red
      dark: '#B91C1C',      // Dark error red
      darker: '#991B1B',    // Darker error red
      light: '#FEE2E2',     // Light error background
      lighter: '#FEF2F2',   // Lighter error background
      alt: '#E50914',       // Alternative error red
      border: '#FECACA',    // Error border
    },
    success: {
      main: '#166534',      // Success green text
      light: '#DCFCE7',     // Success green background
      lighter: '#F0FDF4',   // Lighter success background
      border: '#BBF7D0',    // Success green border
    },
    warning: {
      main: '#92400E',      // Warning orange text
      light: '#FEF3C7',     // Warning yellow background
      border: '#FDE68A',    // Warning border
    },
    info: {
      main: '#1D4ED8',      // Info blue text
      light: '#DBEAFE',     // Info blue background
      border: '#BFDBFE',    // Info blue border
    },
  },

  // Border colors
  border: {
    light: '#E5E7EB',       // Most common border (appears 20+ times)
    medium: '#D1D5DB',      // Medium gray borders
    error: '#EF4444',       // Error borders
    success: '#BBF7D0',     // Success borders
    warning: '#FDE68A',     // Warning borders
    focus: '#FCA5A5',       // Focus/attention borders
  },

  // Interactive states
  interactive: {
    hover: '#F3F4F6',       // General hover background
    focus: '#6554C0',       // Focus color (same as primary)
    disabled: '#E5E7EB',    // Disabled state
  },

  // Gradients (for backgrounds and decorative elements)
  gradients: {
    marketplaceHero: 'linear-gradient(135deg, #c44569 0%, #AC1218 50%, #8B0E14 100%)',
  },

  // Chip/Badge colors (for match type indicators)
  chip: {
    exact: {
      background: '#DCFCE7',
      text: '#166534',
    },
    close: {
      background: '#FEF3C7',
      text: '#92400E',
    },
    far: {
      background: '#FEE2E2',
      text: '#991B1B',
    },
    seo_relevant_topic: {
      background: '#F9FAFB',  // colors.background.light
      text: '#475569',        // colors.primary.main
      border: '#475569',      // colors.primary.main
    },
  },

  // Decorative chip colors (for visual variety in non-semantic contexts)
  decorative: {
    purple: {
      background: '#F8F9FF',  // Light purple background
      text: '#6554C0',        // Purple text (keeping original purple for variety)
      border: '#E0E7FF',
    },
    blue: {
      background: '#EFF6FF',
      text: '#2563EB',
      border: '#DBEAFE',
    },
    green: {
      background: '#ECFDF5',
      text: '#059669',
      border: '#D1FAE5',
    },
    orange: {
      background: '#FFF7ED',
      text: '#EA580C',
      border: '#FED7AA',
    },
    pink: {
      background: '#FDF2F8',
      text: '#DB2777',
      border: '#FBCFE8',
    },
    indigo: {
      background: '#EEF2FF',
      text: '#6366F1',
      border: '#C7D2FE',
    },
    teal: {
      background: '#F0FDFA',
      text: '#0D9488',
      border: '#CCFBF1',
    },
    gray: {
      background: '#F9FAFB',  // colors.background.light
      text: '#374151',        // colors.text.secondary
      border: '#E5E7EB',      // colors.border.light
    },
    red: {
      background: '#FEF2F2',
      text: '#DC2626',
      border: '#FECACA',
    },
  },

  // Switch/Toggle colors
  switch: {
    track: '#D1D5DB',       // Neutral track
    trackActive: '#475569', // Primary slate track when on (consistent with brand)
  },

  // Segmented control colors
  segmented: {
    active: {
      background: '#F3F4F6',  // colors.background.medium
      text: '#374151',        // colors.text.secondary
    },
    inactive: {
      text: '#6B7280',        // colors.text.tertiary
    },
    border: '#E5E7EB',        // colors.border.light
    hover: {
      active: '#D1D5DB',      // colors.border.medium
      inactive: '#F9FAFB',    // colors.background.light
    },
  },

  // Shadow system for consistent elevation
  shadows: {
    // Modern card shadow used in redesigned components
    card: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    // Subtle button shadow
    button: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    // Modal/dialog shadow
    modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    // Small element shadow
    small: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    // Medium elevation shadow
    medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    // Large elevation shadow
    large: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    // No shadow
    none: 'none',
  },

  // Overlay & translucency tokens (centralized rgba usage)
  overlay: {
    white15: 'rgba(255,255,255,0.15)',
    white25: 'rgba(255,255,255,0.25)',
    white95: 'rgba(255,255,255,0.95)',
    black10: 'rgba(0,0,0,0.10)',
    black15: 'rgba(0,0,0,0.15)',
    black25: 'rgba(0,0,0,0.25)',
    black30: 'rgba(0,0,0,0.30)',
    black50: 'rgba(0,0,0,0.50)',
    black70: 'rgba(0,0,0,0.70)',
  },

  // Accent brand/supporting colors (used in hero trust badges)
  accent: {
    green: '#4CAF50',   // Map later to success if needed
    blue: '#2196F3',    // Distinct info accent
    orange: '#FF9800',  // Distinct warning accent
  },

  // Text shadow presets for consistent usage
  effects: {
    textShadow: {
      soft: '0 2px 4px rgba(0,0,0,0.3)',
      strong: '0 2px 4px rgba(0,0,0,0.5)',
      subtle: '0 1px 2px rgba(0,0,0,0.3)',
    },
    boxShadow: {
      deepHero: '0 20px 60px rgba(0,0,0,0.30)',
      card: '0 2px 8px rgba(0,0,0,0.1)',
      banner: '0 -2px 10px rgba(0,0,0,0.1)',
    }
  },
};

// Color utilities for common patterns
export const getStatusColor = (status) => {
  switch (status) {
    case 'error':
      return colors.status.error.main;
    case 'success':
      return colors.status.success.main;
    case 'warning':
      return colors.status.warning.main;
    default:
      return colors.text.tertiary;
  }
};

export const getChipColors = (type) => {
  // Handle all match types including seo_relevant_topic
  switch (type) {
    case 'exact':
      return colors.chip.exact;
    case 'close':
      return colors.chip.close;
    case 'far':
      return colors.chip.far;
    case 'seo_relevant_topic':
      return colors.chip.seo_relevant_topic;
    default:
      return colors.chip.far; // Fallback
  }
};

// Get decorative chip colors for visual variety (non-semantic)
export const getDecorativeChipColors = (index) => {
  const variants = ['purple', 'blue', 'green', 'orange', 'pink', 'indigo', 'teal', 'gray'];
  const colorKey = variants[index % variants.length];
  return colors.decorative[colorKey];
};
